import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  RealtimeClientEvents,
  RealtimeServerEvents,
  type ReadPayload,
  type TypingPayload,
} from '@munifocus/realtime';
import {
  attachReconnectPolicy,
  createRealtimeSocket,
  joinConversation,
  requestDesktopNotifications,
  showDesktopNotification,
  type Socket,
} from '@munifocus/realtime/client';
import {
  applyReadReceiptToPage,
  tombstoneMessage,
  upsertMessage,
  withViewerMine,
  type ChatConversationPage,
  type ChatConversationView,
  type ChatMessageView,
  type MessagePages,
} from '@munifocus/chat';
import { api, apiBaseUrl } from './api-client';
import { chatKeys } from './chat-api';
import { notificationKeys } from './notifications-api';
import type {
  SafetyAlertPayload,
  SafetyEventView,
  OfficerDutyPayload,
  OfficerPositionPayload,
  OfficerPositionView,
  VisitDetail,
  VisitUpdatedPayload,
} from '@munifocus/contracts-gov';
import { mergeOfficer, trackingKeys } from './tracking-api';
import { mergeVisit, visitKeys } from './visits-api';
import { mergeEvent, safetyKeys } from './safety-api';

interface RealtimeContextValue {
  connected: boolean;
  /**
   * Ask the browser for permission to raise desktop banners.
   *
   * Exposed rather than called on mount: browsers refuse a request untied to a
   * user gesture, and a prompt thrown at someone who has just signed in earns a
   * permanent block. The Messages page calls it, because navigating there is the
   * gesture.
   */
  enableDesktopNotifications(): Promise<void>;
  /** Subscribe to a thread's typing/read signals for as long as it is on screen. */
  watchConversation(conversationId: string): () => void;
  /** Who is currently typing, by conversation id. */
  typingBy(conversationId: string): string[];
  setTyping(conversationId: string, typing: boolean): void;
  /**
   * Receive a municipality's officer positions for as long as the live team
   * map is on screen. The server checks `tracking:view` before joining; a
   * refusal simply means nothing arrives and the map falls back to polling.
   */
  watchTracking(localGovernmentId: string): () => void;
  /**
   * Receive a municipality's safety timers and alerts for as long as the
   * safety board is on screen. The server checks `safety:respond` first.
   * Alerts addressed to this user arrive regardless, on their own room.
   */
  watchSafety(localGovernmentId: string): () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * Holds the portal's one socket and folds what it pushes into the query cache.
 *
 * **Why one socket for the whole app.** Every component that cares about live
 * data reads it from React Query, which is already shared. A socket per
 * component would multiply connections by the number of mounted panels and
 * deliver the same message several times over.
 *
 * **Why this writes to the cache rather than exposing messages.** The bell, the
 * conversation list and the open thread are all views of server state that
 * already have query keys. Pushing into those keys means a live update and a
 * refetch produce the same result, and no component needs to know which one it
 * is looking at.
 *
 * The socket carries the session cookie automatically, so there is no token to
 * pass — and nothing to refresh here when it rotates.
 */
export function RealtimeProvider({
  children,
  enabled,
  currentUserId: userId,
  notificationCopy,
}: {
  children: ReactNode;
  /** Who is signed in — used to tell one's own messages from everyone else's. */
  currentUserId: string | undefined;
  /**
   * Localised copy for the bell's desktop banner.
   *
   * Passed in because this module has no i18n context and the push payload
   * carries no text — the inbox owns the bilingual copy, and re-rendering it
   * here would be a second rendering path that only runs for people who are
   * online.
   */
  notificationCopy: { title: string; body: string };
  /**
   * Only connect once the session is established.
   *
   * The gateway drops an unauthenticated handshake, and the client reconnects
   * forever by design — so opening one on the login screen produces an endless
   * connect/reject loop against the API for as long as the tab sits there.
   */
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  // Held in refs so the socket effect does not tear down and rebuild the
  // connection every time the signed-in user or the language changes.
  const currentUserId = useRef(userId);
  currentUserId.current = userId;
  const notificationLabels = useRef(notificationCopy);
  notificationLabels.current = notificationCopy;
  // Conversation id → (user id → display name). Ref, not state: a keystroke from
  // someone else must not re-render the whole tree, only the thread watching it.
  const [typing, setTypingState] = useState<Record<string, Record<string, string>>>({});
  const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // The rooms this client wants to be in, so they can be re-joined on reconnect.
  const watched = useRef<Set<string>>(new Set());
  // Municipalities whose tracking room this tab wants, re-joined on reconnect.
  const trackingWatched = useRef<Set<string>>(new Set());
  // Municipalities whose safety room this tab wants, re-joined on reconnect.
  const safetyWatched = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    // The first connect has nothing to catch up on — the queries just loaded.
    let hasConnected = false;
    const socket = createRealtimeSocket({ baseUrl: apiBaseUrl });
    socketRef.current = socket;
    // A handshake refused because the access cookie expired — a laptop that
    // slept through its fifteen minutes — is never retried by socket.io itself.
    // Any authenticated read rotates the cookie through the API client's 401
    // refresh, and then the policy reconnects.
    const detachReconnect = attachReconnectPolicy(socket, {
      refreshCredentials: () => api.get('/auth/me'),
      shouldReconnect: () => socketRef.current === socket,
    });

    socket.on('connect', () => {
      setConnected(true);
      // Rooms live on the server side of a connection: a socket that dropped and
      // came back has silently left every room it was in, and typing indicators
      // would just stop arriving with nothing to show why.
      for (const conversationId of watched.current) joinConversation(socket, conversationId);
      for (const localGovernmentId of trackingWatched.current) {
        socket.emit(RealtimeClientEvents.TRACKING_SUBSCRIBE, { localGovernmentId });
      }
      for (const localGovernmentId of safetyWatched.current) {
        socket.emit(RealtimeClientEvents.SAFETY_SUBSCRIBE, { localGovernmentId });
      }
      // A safety alert raised while this tab was offline was never pushed.
      if (hasConnected) queryClient.invalidateQueries({ queryKey: safetyKeys.all });
      // Anything that changed while disconnected was never pushed, so re-read
      // rather than trusting a cache that has a hole in it of unknown size.
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox });
      // Thread history too, not just the list. The open thread refetches now;
      // any other cached thread is marked stale and refetches when reopened.
      // Refreshing only the list left the transcript on screen missing every
      // message sent during the gap, with the list's preview showing the last
      // one — the officer could see a message existed and not read it.
      if (hasConnected) queryClient.invalidateQueries({ queryKey: chatKeys.allMessages });
      hasConnected = true;
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on(RealtimeServerEvents.MESSAGE_NEW, (pushed: ChatMessageView) => {
      // `mine` is forced false on the wire for everyone, so it is resolved here
      // from the author id — otherwise this user's own message, sent from
      // another tab or device, would be drawn as someone else's.
      const message = withViewerMine(pushed, currentUserId.current);
      if (!message.mine && !isConversationMuted(message.conversationId)) {
        showDesktopNotification({
          title: message.authorName,
          body: message.body || attachmentSummary(message),
          // One banner per conversation: a colleague typing five lines should
          // replace one banner, not stack five.
          tag: `chat:${message.conversationId}`,
          onClick: () => {
            window.location.assign(`/messages?conversation=${message.conversationId}`);
          },
        });
      }

      queryClient.setQueryData<MessagePages>(chatKeys.messages(message.conversationId), (prev) =>
        // Only merge into a thread already in cache. Seeding one from a single
        // pushed message would leave a page with no history behind it, which
        // renders as a thread that appears to start mid-conversation.
        prev ? upsertMessage(prev, message) : prev,
      );
      // Their own message stops them typing, and the server sends no stop event.
      clearTyping(message.conversationId, message.author?.id);
    });

    socket.on(RealtimeServerEvents.MESSAGE_UPDATED, (pushed: ChatMessageView) => {
      const message = withViewerMine(pushed, currentUserId.current);
      queryClient.setQueryData<MessagePages>(chatKeys.messages(message.conversationId), (prev) =>
        prev ? upsertMessage(prev, message) : prev,
      );
    });

    socket.on(
      RealtimeServerEvents.MESSAGE_DELETED,
      ({ conversationId, messageId }: { conversationId: string; messageId: string }) => {
        // Tombstoned in place, not removed: dropping it would reflow the thread
        // around a gap and lose the record that something was said.
        queryClient.setQueryData<MessagePages>(chatKeys.messages(conversationId), (prev) =>
          tombstoneMessage(prev, messageId, new Date().toISOString()),
        );
      },
    );

    socket.on(RealtimeServerEvents.CONVERSATION_UPDATED, (conversation: ChatConversationView) => {
      queryClient.setQueryData<ChatConversationPage>(chatKeys.conversations, (prev) =>
        mergeConversation(prev, conversation),
      );
    });

    // Someone read a thread this user is in. The "seen" marker under one's own
    // messages is derived from members' `lastReadAt`, so moving it here is what
    // makes the tick appear live rather than on the next refetch.
    socket.on(RealtimeServerEvents.READ, (receipt: ReadPayload) => {
      queryClient.setQueryData<ChatConversationPage>(chatKeys.conversations, (prev) =>
        applyReadReceiptToPage(prev, receipt),
      );
    });

    socket.on(RealtimeServerEvents.TYPING, (payload: TypingPayload) => {
      if (payload.typing) {
        setTypingState((prev) => ({
          ...prev,
          [payload.conversationId]: {
            ...prev[payload.conversationId],
            [payload.userId]: payload.displayName,
          },
        }));
        // Expire it locally. A stop event can be lost — the sender may simply
        // close the tab — and an indicator with no timeout would say someone is
        // typing until the page is reloaded.
        const key = `${payload.conversationId}:${payload.userId}`;
        clearTimeout(typingTimers.current[key]);
        typingTimers.current[key] = setTimeout(
          () => clearTyping(payload.conversationId, payload.userId),
          5_000,
        );
      } else {
        clearTyping(payload.conversationId, payload.userId);
      }
    });

    socket.on(RealtimeServerEvents.NOTIFICATION_NEW, () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox });
      // No copy is pushed — the payload is a hint and the inbox owns the
      // bilingual text — so the banner says only that something arrived and
      // sends them to the portal to read it in their own language.
      showDesktopNotification({
        title: notificationLabels.current.title,
        body: notificationLabels.current.body,
        tag: 'notification',
        onClick: () => window.location.assign('/'),
      });
    });

    // Officer positions arrive only for the municipalities this tab joined,
    // and only after the server checked tracking:view there.
    socket.on(RealtimeServerEvents.OFFICER_POSITION, (payload: OfficerPositionPayload) => {
      queryClient.setQueryData<OfficerPositionView[]>(
        trackingKeys.live(payload.localGovernmentId),
        (prev) => mergeOfficer(prev, payload.officer),
      );
    });
    socket.on(RealtimeServerEvents.OFFICER_DUTY, (payload: OfficerDutyPayload) => {
      if (payload.onDuty) return; // the first position draws the marker
      queryClient.setQueryData<OfficerPositionView[]>(
        trackingKeys.live(payload.localGovernmentId),
        (prev) => prev?.filter((o) => o.officerId !== payload.officerId),
      );
    });

    // Officer safety. An alert is kept for the shell banner (it may concern a
    // municipality other than the one on screen) and everything safety-shaped
    // is re-read: the board, the officer's own timer and SOS status.
    socket.on(RealtimeServerEvents.SAFETY_ALERT, (payload: SafetyAlertPayload) => {
      queryClient.setQueryData<SafetyEventView[]>(safetyKeys.pushed, (prev) =>
        mergeEvent(prev, payload.event),
      );
      void queryClient.invalidateQueries({
        queryKey: safetyKeys.all,
        predicate: (q) => q.queryKey[1] !== 'pushed',
      });
    });
    socket.on(RealtimeServerEvents.SAFETY_TIMER, () => {
      void queryClient.invalidateQueries({
        queryKey: safetyKeys.all,
        predicate: (q) => q.queryKey[1] === 'me' || q.queryKey[1] === 'board',
      });
    });

    // A visit on a file this officer works changed status. Only folded into a
    // list already on screen; an unopened file reads it fresh when opened.
    socket.on(RealtimeServerEvents.VISIT_UPDATED, (payload: VisitUpdatedPayload) => {
      queryClient.setQueryData<VisitDetail[]>(visitKeys.byFile(payload.fileId), (prev) =>
        prev ? mergeVisit(prev, payload.visit) : prev,
      );
    });

    return () => {
      for (const timer of Object.values(typingTimers.current)) clearTimeout(timer);
      detachReconnect();
      socket.close();
      socketRef.current = null;
    };

    /**
     * Whether the user has silenced this thread. The bell and the badge still
     * count it — muting is about interruptions — but a desktop banner is
     * exactly the interruption they asked not to get.
     */
    function isConversationMuted(conversationId: string): boolean {
      const conversation = queryClient
        .getQueryData<ChatConversationPage>(chatKeys.conversations)
        ?.conversations.find((c) => c.id === conversationId);
      const until = conversation?.mutedUntil;
      return Boolean(until && new Date(until) > new Date());
    }

    function clearTyping(conversationId: string, userId?: string) {
      if (!userId) return;
      setTypingState((prev) => {
        const room = prev[conversationId];
        if (!room?.[userId]) return prev;
        const { [userId]: _removed, ...rest } = room;
        return { ...prev, [conversationId]: rest };
      });
    }
    // Query client identity is stable for the app's lifetime, so this only
    // re-runs when the session appears or disappears — which is exactly when
    // the socket should be opened or torn down.
  }, [queryClient, enabled]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      connected,
      watchConversation(conversationId: string) {
        watched.current.add(conversationId);
        const socket = socketRef.current;
        const leave = socket ? joinConversation(socket, conversationId) : () => undefined;
        return () => {
          watched.current.delete(conversationId);
          leave();
        };
      },
      async enableDesktopNotifications() {
        await requestDesktopNotifications();
      },
      typingBy: (conversationId: string) => Object.values(typing[conversationId] ?? {}),
      watchTracking(localGovernmentId: string) {
        trackingWatched.current.add(localGovernmentId);
        socketRef.current?.emit(RealtimeClientEvents.TRACKING_SUBSCRIBE, { localGovernmentId });
        return () => {
          trackingWatched.current.delete(localGovernmentId);
          socketRef.current?.emit(RealtimeClientEvents.TRACKING_UNSUBSCRIBE, { localGovernmentId });
        };
      },
      watchSafety(localGovernmentId: string) {
        safetyWatched.current.add(localGovernmentId);
        socketRef.current?.emit(RealtimeClientEvents.SAFETY_SUBSCRIBE, { localGovernmentId });
        return () => {
          safetyWatched.current.delete(localGovernmentId);
          socketRef.current?.emit(RealtimeClientEvents.SAFETY_UNSUBSCRIBE, { localGovernmentId });
        };
      },
      setTyping(conversationId: string, isTyping: boolean) {
        socketRef.current?.emit(isTyping ? 'typing.start' : 'typing.stop', { conversationId });
      },
    }),
    [connected, typing],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used inside a RealtimeProvider');
  return context;
}

/** Upserts one conversation into the cached list, keeping it most-recent-first. */
function mergeConversation(
  page: ChatConversationPage | undefined,
  conversation: ChatConversationView,
): ChatConversationPage {
  const existing = page?.conversations ?? [];
  const without = existing.filter((c) => c.id !== conversation.id);
  const conversations = [conversation, ...without].sort(byRecency);
  return {
    conversations,
    // Recomputed rather than carried over: the badge is the sum of these rows,
    // and leaving the old total would contradict what the list now shows.
    totalUnread: conversations.reduce((sum, c) => sum + c.unreadCount, 0),
  };
}

function byRecency(a: ChatConversationView, b: ChatConversationView): number {
  const left = a.lastMessageAt ?? '';
  const right = b.lastMessageAt ?? '';
  return right.localeCompare(left);
}

/** One line describing what was shared, when a message carries no text. */
function attachmentSummary(message: ChatMessageView): string {
  const [first] = message.attachments;
  if (!first) return '';
  return message.attachments.length > 1
    ? `${first.fileName} +${message.attachments.length - 1}`
    : first.fileName;
}
